import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Outlet, useLocation, useNavigate, useOutlet } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faArrowRightFromBracket } from '@fortawesome/free-solid-svg-icons';
import './index.scss';

import { useMultipleTabs, useNavigationWarning, useRefreshToken, useScreenWidth } from '../../shared/Helpers/hooks';
import { MENU } from '../../App/constants';
import { CustomButton, Text } from '../../shared/uiComponents';
import { IlluminateLogo, Logo, Sidebar } from '../../shared/Assets/svgIcons';
import { capitalizer } from '../../shared/Helpers/functions';
import { PEARL, STROKE } from '../../shared/Helpers/colors';
import {
	AppBar,
	AppBarMobile,
	Container,
	DrawerHeader,
	DrawerWrapper,
	Main,
	TopNavigationPlaceholder,
	Wrapper,
} from './styledComponents';
import MenuElements, { MobileMenu } from './menuElements';
import { Loader } from '../StyledComponents';
import SavedDraftsInfo from './savedDraftsInfo';
import FeedbackButton from './feedbackButton';
import UnsavedTargetsWarning from '../../pages/Calendar/Session/DataCollection/UnsavedTargetsWarning';
// import LegacyNavigationInfo from "./legacyNavigationInfo";

import { DispatchProperties, useSelector } from '../../redux/store';
import { resetState } from '../../redux/resetState';
import { getAccountRoles, refresh } from '../../redux/State/identitySlice/accountSlice';
import { getRefreshToken } from '../../redux/API/Main/helpers';
import { setIsMenuOpen } from '../../redux/State/commonSlice';
import BranchFilter from './branchFilter';

export enum AdminTypes {
	'admin',
	'bcba',
	'bt',
	'cs',
}

const Layout = () => {
	useRefreshToken();
	useMultipleTabs();

	const navigate = useNavigate();
	const { pathname } = useLocation();
	const screenWidth = useScreenWidth();
	const outlet = useOutlet();
	const dispatch = useDispatch<DispatchProperties>();

	const isMobile = screenWidth < 768;

	const open = useSelector((state) => state.common.isMenuOpen);

	const role = useSelector((state) => state.account.role);
	const user = useSelector((state) => state.account.user);
	const loading = useSelector((state) => state.account.loading);

	const isAdmin = role?.section.id === AdminTypes.admin;
	const isBCBA = role?.section.id === AdminTypes.bcba;
	const isBT = role?.section.id === AdminTypes.bt;
	const isCS = role?.section.id === AdminTypes.cs;

	const { showWarning, checkAndNavigate, handleWarningConfirm, handleWarningCancel } = useNavigationWarning();

	useEffect(() => {
		const token = getRefreshToken();
		if (Boolean(user.id) || !token) return;
		dispatch(refresh(token));
	}, [user, dispatch]);

	useEffect(() => {
		if (role) return;
		dispatch(getAccountRoles());
	}, [role, dispatch]);

	useEffect(() => {
		if (!outlet) navigate('/dashboard');
	}, [outlet, navigate]);

	const logOutHandler = () => {
		checkAndNavigate(() => {
			dispatch(resetState());
			navigate('/login');
		});
	};

	const toggleMenuHandler = () => dispatch(setIsMenuOpen(!open));

	const navigateToDashboardHandler = () => {
		if (role?.section.id === AdminTypes.bt) return;
		checkAndNavigate(() => navigate('/dashboard'));
	};

	const navigateBackHandler = () => {
		checkAndNavigate(() => navigate(-1));
	};

	const navigateForwardHandler = () => {
		checkAndNavigate(() => navigate(1));
	};

	const pageTitle = MENU.ADMIN.find((x) => pathname.includes(x.url))?.title;

	return (
		<Container>
			{isMobile ? (
				<AppBarMobile>
					<div onClick={navigateToDashboardHandler} style={{ cursor: 'pointer' }}>
						<Logo />
					</div>
					<div className="mobileHeaderActions">
						<SavedDraftsInfo />
						<FeedbackButton />
						<BranchFilter />
						<div
							style={{
								height: '24px',
								width: '1px',
								backgroundColor: 'rgba(0, 0, 0, 0.12)',
								borderRadius: '2px',
							}}
						/>
						<CustomButton
							startIcon={<FontAwesomeIcon icon={faArrowRightFromBracket} color="#D64526" />}
							title={isMobile ? '' : 'Log Out'}
							buttonType="tertiary"
							onClick={logOutHandler}
						/>
					</div>
				</AppBarMobile>
			) : (
				<AppBar open={open}>
					<CustomButton
						onClick={navigateBackHandler}
						buttonType="secondaryGray"
						iconOnly={<FontAwesomeIcon icon={faArrowLeft} color={PEARL.BASE[600]} fontSize={14} fontWeight={400} />}
					/>
					{screenWidth > 768 && (
						<CustomButton
							onClick={navigateForwardHandler}
							buttonType="secondaryGray"
							iconOnly={
								<FontAwesomeIcon
									icon={faArrowRight}
									color={PEARL.BASE[600]}
									fontSize={14}
									fontWeight={400}
									height={20}
								/>
							}
						/>
					)}
					<div
						style={{
							height: '16px',
							width: '1px',
							backgroundColor: STROKE.TERTIARY,
						}}
					/>
					<Text
						title={pageTitle ? capitalizer(pageTitle) : ''}
						type="psm"
						style={{
							flex: '1 0 0',
						}}
					/>
					<div
						style={{
							display: 'flex',
							gap: '12px',
							alignItems: 'center',
						}}
					>
						<SavedDraftsInfo />
						<FeedbackButton />
						<BranchFilter />
						<div
							style={{
								height: '24px',
								width: '1px',
								backgroundColor: 'rgba(0, 0, 0, 0.12)',
								borderRadius: '2px',
							}}
						/>
						<CustomButton
							startIcon={<FontAwesomeIcon icon={faArrowRightFromBracket} color="#D64526" />}
							title={isMobile ? '' : 'Log Out'}
							buttonType="tertiary"
							onClick={logOutHandler}
						/>
					</div>
				</AppBar>
			)}
			{!isMobile && (
				<DrawerWrapper
					variant="permanent"
					anchor="left"
					open={open}
					PaperProps={{
						style: {
							overflow: 'auto',
						},
					}}
				>
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							flexDirection: 'column',
							height: '100%',
						}}
					>
						<div>
							<DrawerHeader open={open}>
								{open && (
									<div onClick={navigateToDashboardHandler} style={{ cursor: 'pointer' }}>
										<IlluminateLogo />
									</div>
								)}
								<CustomButton iconOnly={Sidebar} buttonType="secondaryGray" onClick={toggleMenuHandler} />
							</DrawerHeader>
							<div style={{ padding: '16px', width: '100%' }}>
								{loading ? (
									<Loader />
								) : (
									<>
										{isAdmin && <MenuElements menu={MENU.ADMIN} open={open} checkAndNavigate={checkAndNavigate} />}
										{isBCBA && <MenuElements menu={MENU.BCBA} open={open} checkAndNavigate={checkAndNavigate} />}
										{isBT && <MenuElements menu={MENU.BT} open={open} checkAndNavigate={checkAndNavigate} />}
										{isCS && <MenuElements menu={MENU.CS} open={open} checkAndNavigate={checkAndNavigate} />}
									</>
								)}
							</div>
						</div>
						{/* <LegacyNavigationInfo /> */}
					</div>
				</DrawerWrapper>
			)}
			{isMobile && <MobileMenu checkAndNavigate={checkAndNavigate} />}
			<Main open={open}>
				<TopNavigationPlaceholder open={open} />
				<Wrapper>
					<Outlet />
				</Wrapper>
			</Main>
			<UnsavedTargetsWarning open={showWarning} onCancel={handleWarningCancel} onConfirm={handleWarningConfirm} />
		</Container>
	);
};

export default Layout;
